import React, { useCallback, useEffect, useRef, useState } from "react";
import { FiChevronLeft, FiChevronRight } from "react-icons/fi";
import { logos } from "./logopartner";
import "./TrustedLogoSlider.css";

export default function TrustedLogoSlider() {
  const trackRef = useRef(null);
  const [isHovered, setIsHovered] = useState(false);
  const [isFocused, setIsFocused] = useState(false);

  const slide = useCallback((direction) => {
    const track = trackRef.current;
    if (!track) return;
    const end = track.scrollWidth - track.clientWidth;
    let left = track.scrollLeft + direction * track.clientWidth;
    if (direction > 0 && track.scrollLeft >= end - 2) left = 0;
    if (direction < 0 && track.scrollLeft <= 2) left = end;
    track.scrollTo({
      left,
      behavior: window.matchMedia("(prefers-reduced-motion: reduce)").matches ? "auto" : "smooth",
    });
  }, []);

  useEffect(() => {
    if (isHovered || isFocused) return;

    const motionPreference = window.matchMedia("(prefers-reduced-motion: reduce)");
    let frame;
    let previousTime = null;
    let position = trackRef.current?.scrollLeft || 0;

    function animate(time) {
      const track = trackRef.current;
      if (track && !document.hidden && !motionPreference.matches) {
        const first = track.children[0];
        const duplicate = track.children[logos.length];
        const loopWidth = first && duplicate
          ? duplicate.getBoundingClientRect().left - first.getBoundingClientRect().left
          : 0;

        // Keep fractional pixels so the speed stays consistent on all displays.
        if (Math.abs(track.scrollLeft - position) > 1) position = track.scrollLeft;
        if (previousTime !== null && loopWidth > 0) {
          position = (position + Math.min(time - previousTime, 50) * 0.08) % loopWidth;
          track.scrollLeft = position;
        }
        previousTime = time;
      } else {
        previousTime = null;
      }
      frame = window.requestAnimationFrame(animate);
    }

    frame = window.requestAnimationFrame(animate);

    return () => window.cancelAnimationFrame(frame);
  }, [isHovered, isFocused]);

  return (
    <section className="trusted-logo-strip" aria-labelledby="trusted-logo-heading" aria-roledescription="carousel">
      <div className="home-content mt-4 mb-4">
        <h2 id="trusted-logo-heading">Trusted by 1,000+ businesses worldwide</h2>
        <p className="trusted-logo-description">From startups to enterprises, leading brands trust Seobix for their SEO growth.</p>
        <div
          className="trusted-logo-carousel"
          onMouseEnter={() => setIsHovered(true)}
          onMouseLeave={() => setIsHovered(false)}
          onFocus={() => setIsFocused(true)}
          onBlur={(event) => {
            if (!event.currentTarget.contains(event.relatedTarget)) setIsFocused(false);
          }}
        >
          <button type="button" onClick={() => slide(-1)} aria-label="Previous client logos" aria-controls="trusted-logo-track"><FiChevronLeft /></button>
          <ul className="trusted-logo-track" id="trusted-logo-track" ref={trackRef} tabIndex={0} aria-label="Client logos">
            {logos.map(logo => (
              <li key={logo.alt}><img src={logo.src} alt={logo.alt} width="160" height="56" loading="lazy" decoding="async" /></li>
            ))}
            {logos.map(logo => (
              <li key={`duplicate-${logo.alt}`} aria-hidden="true"><img src={logo.src} alt="" width="160" height="56" loading="lazy" decoding="async" /></li>
            ))}
          </ul>
          <button type="button" onClick={() => slide(1)} aria-label="Next client logos" aria-controls="trusted-logo-track"><FiChevronRight /></button>
        </div>
      </div>
    </section>
  );
}
